import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FileUp, Loader2, Info, Type, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  extractTextFromPentestFile,
  normalizePastedText,
  combineReportTexts,
  isAcceptedPentestFile,
  getFileExtension,
  requestPentestExtraction,
  PENTEST_MAX_FILES,
} from '../../../../services/pentestImportService';
import { showErrorToast } from '../../../../utils/toastUtils';

const STEPS = ['reading', 'extracting', 'mapping'];

/**
 * PentestImportButton — entry card for old pentest reports.
 * Two input modes: file upload (txt/md/csv/pdf) or pasted text.
 * Path-independent: findings attach to business supports, not attack paths.
 * Extracts text client-side then calls AI extraction endpoint.
 */
const PentestImportButton = ({ analysisId, businessAssets, disabled, onBusyChange, onExtracted }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [isWorking, setIsWorking] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [fileName, setFileName] = useState(null);
  const [mode, setMode] = useState('file');
  const [pastedText, setPastedText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    onBusyChange && onBusyChange(isWorking);
  }, [isWorking, onBusyChange]);

  const dedupeFiles = (list) => {
    const seen = new Set();
    return list.filter((f) => {
      const key = `${f.name}|${f.size}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const errorMessage = (code) => {
    switch (code) {
      case 'too_big':
        return t('workshop4.pentest.tooBig', 'Fichier trop volumineux (max 5 Mo).');
      case 'bad_type':
        return t('workshop4.pentest.badType', 'Formats acceptés : .txt, .pdf, .csv.');
      case 'scanned_pdf':
        return t('workshop4.pentest.scannedPdf', 'PDF scanné sans texte extractible. Exportez le rapport en PDF texte ou en .txt.');
      case 'empty':
        return t('workshop4.pentest.empty', 'Fichier vide ou illisible.');
      case 'too_short':
        return t('workshop4.pentest.tooShort', 'Texte trop court pour être analysé (min 20 caractères).');
      default:
        return t('workshop4.pentest.extractError', "L'extraction a échoué. Veuillez contacter ACG pour assistance.");
    }
  };

  // Staged selection: picking files only stages them, analysis starts on demand.
  const handleFilesSelected = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length === 0) return;
    let list = dedupeFiles([...selectedFiles, ...picked]);
    if (list.length > PENTEST_MAX_FILES) {
      showErrorToast(t('workshop4.pentest.tooManyFiles', 'Maximum 5 fichiers à la fois.'));
      list = list.slice(0, PENTEST_MAX_FILES);
    }
    const valid = [];
    list.forEach((file) => {
      if (!isAcceptedPentestFile(file)) {
        const reason = ['.txt', '.md', '.csv', '.pdf'].includes(getFileExtension(file.name))
          ? errorMessage('too_big')
          : errorMessage('bad_type');
        showErrorToast(t('workshop4.pentest.fileSkipped', 'Ignoré : {{name}} ({{reason}})', {
          name: file.name, reason,
        }));
        return;
      }
      valid.push(file);
    });
    setSelectedFiles(valid);
  };

  const handleAnalyzeFiles = async () => {
    if (!analysisId) {
      showErrorToast(t('workshop4.status.noAnalysisSelected'));
      return;
    }
    if (selectedFiles.length === 0) {
      showErrorToast(t('workshop4.pentest.noFiles', 'Sélectionnez au moins un fichier.'));
      return;
    }
    setIsWorking(true);
    setStepIndex(0);
    try {
      const parts = [];
      const skipped = [];
      for (const file of selectedFiles) {
        setFileName(file.name);
        try {
          parts.push({ filename: file.name, text: await extractTextFromPentestFile(file) });
        } catch (err) {
          console.error('[PentestImport] file extraction failed:', file.name, err);
          skipped.push(file.name);
        }
      }
      if (parts.length === 0) {
        showErrorToast(t('workshop4.pentest.allSkipped', 'Aucun fichier exploitable.'));
        return;
      }
      if (skipped.length > 0) {
        showErrorToast(t('workshop4.pentest.someSkipped', '{{count}} fichier(s) ignoré(s) : {{names}}', {
          count: skipped.length, names: skipped.join(', '),
        }));
      }
      setStepIndex(1);
      const { rawText, truncated } = combineReportTexts(parts);
      const filename = parts.map((p) => p.filename).join(' + ').slice(0, 200);
      const data = await requestPentestExtraction({
        analysisId,
        filename,
        rawText,
        businessAssets,
      });
      setStepIndex(2);
      onExtracted && onExtracted({
        findings: data.findings || [],
        techniques: data.techniques || [],
        filename,
        source: filename,
        truncated: truncated || data.truncated,
        droppedTechniques: data.droppedTechniques || 0,
      });
      setSelectedFiles([]);
    } catch (err) {
      console.error('[PentestImport] files extraction failed:', err);
      const code = err?.message;
      showErrorToast(errorMessage(code));
    } finally {
      setIsWorking(false);
      setFileName(null);
    }
  };

  const handleAnalyzeText = async () => {
    if (!analysisId) {
      showErrorToast(t('workshop4.status.noAnalysisSelected'));
      return;
    }
    setIsWorking(true);
    setStepIndex(1);
    const filename = t('workshop4.pentest.directText', 'Texte direct');
    setFileName(filename);
    try {
      const rawText = normalizePastedText(pastedText);
      const data = await requestPentestExtraction({
        analysisId,
        filename,
        rawText,
        businessAssets,
      });
      setStepIndex(2);
      onExtracted && onExtracted({
        findings: data.findings || [],
        techniques: data.techniques || [],
        filename,
        source: filename,
        truncated: data.truncated,
        droppedTechniques: data.droppedTechniques || 0,
      });
      setPastedText('');
    } catch (err) {
      console.error('[PentestImport] text extraction failed:', err);
      const code = err?.message;
      showErrorToast(errorMessage(code));
    } finally {
      setIsWorking(false);
      setFileName(null);
    }
  };

  return (
    <div className="mt-4 h-full bg-white rounded-xl border border-purple-200 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-start gap-3">
          <div className="bg-purple-100 p-2 rounded-lg shrink-0">
            <FileUp size={20} className="text-purple-700" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">
              {t('workshop4.pentest.importTitle', 'Vous avez déjà un rapport de scan ?')}
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              {t('workshop4.pentest.importSubtitle', "Importez-le (txt, pdf, csv) : l'Assistant ACG extrait les résultats et les affecte aux biens supports.")}
            </p>
            <div className="flex gap-1.5 mt-2">
              {[
                { id: 'file', label: t('workshop4.pentest.tabFile', 'Fichier'), icon: <FileUp size={13} /> },
                { id: 'text', label: t('workshop4.pentest.tabText', 'Texte'), icon: <Type size={13} /> },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setMode(tab.id)}
                  disabled={isWorking}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${mode === tab.id
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <Info size={12} />
              {t('workshop4.pentest.hint', 'Optionnel — vous pouvez aussi continuer en analyse CTI classique.')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.md,.pdf,.csv"
            multiple
            className="hidden"
            onChange={handleFilesSelected}
            disabled={disabled || isWorking}
          />
          {mode === 'file' ? (
            <div className="flex gap-2">
              <button
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={disabled || isWorking || !analysisId}
                className="text-sm font-medium bg-white border border-purple-300 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-50 disabled:opacity-50 flex items-center transition duration-200"
              >
                <FileUp size={16} className="mr-2" />
                {t('workshop4.pentest.chooseFiles', 'Choisir fichiers')}
              </button>
              <button
                onClick={handleAnalyzeFiles}
                disabled={disabled || isWorking || !analysisId || selectedFiles.length === 0}
                className="text-sm font-medium bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center shadow-sm transition duration-200"
              >
                {isWorking ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t(`workshop4.pentest.steps.${STEPS[stepIndex]}`, fileName || 'Analyse…')}
                  </>
                ) : (
                  <>
                    <FileUp size={16} className="mr-2" />
                    {t('workshop4.pentest.analyzeFiles', 'Analyser {{count}} fichier(s)', { count: selectedFiles.length })}
                  </>
                )}
              </button>
            </div>
          ) : (
            <button
              onClick={handleAnalyzeText}
              disabled={disabled || isWorking || !analysisId || pastedText.trim().length === 0}
              className="text-sm font-medium bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center shadow-sm transition duration-200"
            >
              {isWorking ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {t(`workshop4.pentest.steps.${STEPS[stepIndex]}`, fileName || 'Analyse…')}
                </>
              ) : (
                <>
                  <Type size={16} className="mr-2" />
                  {t('workshop4.pentest.analyzeText', 'Analyser le texte')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
      {mode === 'file' && selectedFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {selectedFiles.map((f) => (
            <span
              key={`${f.name}|${f.size}`}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-800 border border-purple-200"
            >
              {f.name}
              <button
                onClick={() => setSelectedFiles(selectedFiles.filter((x) => x !== f))}
                disabled={isWorking}
                className="hover:text-purple-600 disabled:opacity-50"
                title={t('common.delete', 'Supprimer')}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      {mode === 'file' && (
        <p className="text-xs text-slate-400 mt-2">
          {t('workshop4.pentest.multipleHint', 'Plusieurs fichiers acceptés, formats mélangeables (5 max). Budget total ~40 000 caractères pour garder un bon contexte modèle.')}
        </p>
      )}
      {mode === 'text' && (
        <textarea
          value={pastedText}
          onChange={(e) => setPastedText(e.target.value)}
          disabled={disabled || isWorking}
          rows={6}
          placeholder={t('workshop4.pentest.textPlaceholder', 'Collez ici le contenu du rapport de scan (constats, CVE, statuts corrigé / non corrigé, actifs concernés)…')}
          className="mt-3 w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-50"
        />
      )}
      {!analysisId && (
        <p className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-2 mt-3">
          {t('workshop4.status.noAnalysisSelected')}
        </p>
      )}
    </div>
  );
};

PentestImportButton.propTypes = {
  analysisId: PropTypes.string,
  businessAssets: PropTypes.array,
  disabled: PropTypes.bool,
  onBusyChange: PropTypes.func,
  onExtracted: PropTypes.func,
};

PentestImportButton.defaultProps = {
  businessAssets: [],
  disabled: false,
};

export default PentestImportButton;
